import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { SortDirection } from '@angular/material/sort';
import { CodeSearchParameters, CodeSearchResponse } from '@mtna/classification-service-core-ts';
import { isNullOrUndefined } from '@mtna/core-ts';
import { RelationalOperator } from '@mtna/data-core-ts';
import { CanDestroyNotify, CanDestroyNotifyCtor, mixinDestroyNotifier } from '@mtna/core-angular';
import { MtnaErrorService } from '@mtna/core-angular';
import { Classification, Code, Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { RdsClassificationService } from '@rds/shared-api';
import { BehaviorSubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { RdsDateFilter } from '../core/models/filter/date-filter.model';
import { RdsNullParameter } from '../core/models/filter/null-filter.model';
import { RdsFilterUtil } from '../core/utils/filter-util';
import { RdsNumericFilter } from '../numeric-filter/numeric-filter.model';
import { RdsTextFilter } from '../text-filter/text-filter.model';

// Boilerplate for applying mixins to RdsFilterDialogComponent.
class RdsFilterDialogComponentBase {}
const _RdsFilterDialogComponentMixinBase: CanDestroyNotifyCtor = mixinDestroyNotifier(RdsFilterDialogComponentBase);

export interface RdsFilterDialogOutput {
  filters: Array<Code | RdsDateFilter | RdsNumericFilter | RdsTextFilter>;
  nullFilter: RdsNullParameter;
  excludeSelectedCodes: boolean;
}

export class RdsFilterDialogData<V extends Variable | VariableSummary = Variable> {
  /**
   * @param variable variable to use as a filter
   * @param appliedFilters already applied filters, optional
   * @param appliedNullFilter already applied null filter, optional
   * @param multiSelect whether to allow multiple code selection, defaults to true
   * @param contentStyles custom styles for the dialog content's filter component
   * @param largeChips whether the chips that represent filters should use the large style variant, defaults to true
   * @param supportedFilters the filter operators that the current data product supports
   * @param excludeSelectedCodes Whether to exclude the codes selected (for Code Filter only)
   */
  constructor(
    public variable: V,
    public appliedFilters: Array<Code | RdsDateFilter | RdsNumericFilter | RdsTextFilter> = [],
    public appliedNullFilter: RdsNullParameter = 'NOT_APPLIED',
    public multiSelect: boolean = true,
    public contentStyles: { [key: string]: string } = { height: 'auto' },
    public largeChips: boolean = true,
    public supportedFilters: RelationalOperator[] | undefined,
    public excludeSelectedCodes = false
  ) {
    // Coerce null filter to a non-null value
    if (isNullOrUndefined(appliedNullFilter)) {
      this.appliedNullFilter = 'NOT_APPLIED';
    }
  }
}

/** Subject to maintain current sort direction, with a quick Material Sort => Mtna Sort conversion  */
class SortDirectionSubject extends BehaviorSubject<SortDirection> {
  get valueMTNA(): 'ASC' | 'DESC' | undefined {
    return !this.value.length ? undefined : (this.value.toUpperCase() as 'ASC' | 'DESC');
  }
}

@Component({
  selector: 'rds-c-filter-dialog',
  templateUrl: 'filter-dialog.component.html',
  styleUrls: [`./filter-dialog.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsFilterDialogComponent extends _RdsFilterDialogComponentMixinBase implements OnInit, CanDestroyNotify {
  filterType: 'CODE' | 'DATE' | 'TEXT' | 'NUMERIC' | null;
  nullFilter: RdsNullParameter;
  disableApply$ = new BehaviorSubject<boolean>(true);

  //#region CODE FILTER PROPS -----------------------------
  /** page index * page size */
  codeOffset = 0;
  /** page size for paginating/slicing codes */
  codePageSize = 25;
  /** Codes from search result */
  codes: Array<Code> = [];
  /** Text for code search */
  codeSearchText = '';
  /** Number of codes found for the latest code search */
  foundCodes = 0;
  /** if classification contains 20 or less codes. Defaults to true, in order to hide the searchbox until it's needed. */
  hasFewCodes = true;
  /** toggles the progress bar */
  loadingCodes = false;
  /** codes the user selected */
  excludeSelected$ = new BehaviorSubject<boolean>(false);
  selectedCodes$ = new BehaviorSubject<Array<Code>>([]);
  sortDirection$ = new SortDirectionSubject('');
  //#endregion CODE FILTER PROPS  -------------------------

  //#region OTHER FILTER PROPS -----------------------------
  dateFilters$: BehaviorSubject<RdsDateFilter[]> = new BehaviorSubject<RdsDateFilter[]>([]);
  numericFilters$: BehaviorSubject<RdsNumericFilter[]> = new BehaviorSubject<RdsNumericFilter[]>([]);
  textFilters$: BehaviorSubject<RdsTextFilter[]> = new BehaviorSubject<RdsTextFilter[]>([]);
  //#endregion OTHER FILTER PROPS --------------------------

  get appliedFilters() {
    switch (this.filterType) {
      case 'CODE':
        return this.selectedCodes$.value;
      case 'TEXT':
        return this.textFilters$.value;
      case 'DATE':
        return this.dateFilters$.value;
      case 'NUMERIC':
        return this.numericFilters$.value;
      default:
        return [];
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private classificationService: RdsClassificationService,
    public dialogRef: MatDialogRef<RdsFilterDialogComponent, RdsFilterDialogOutput>,
    @Inject(MAT_DIALOG_DATA) public data: RdsFilterDialogData,
    private errorService: MtnaErrorService
  ) {
    super();
    this.nullFilter = data.appliedNullFilter;

    // Determine filter type
    this.filterType = RdsFilterUtil.determineType(data?.variable);

    if (data?.appliedFilters?.length) {
      switch (this.filterType) {
        case 'CODE':
          this.selectedCodes$.next(data.appliedFilters as Code[]);
          this.excludeSelected$.next(data.excludeSelectedCodes);
          break;
        case 'TEXT':
          this.textFilters$.next(data.appliedFilters as RdsTextFilter[]);
          break;
        case 'DATE':
          this.dateFilters$.next(data.appliedFilters as RdsDateFilter[]);
          break;
        case 'NUMERIC':
          this.numericFilters$.next(data.appliedFilters as RdsNumericFilter[]);
          break;
      }
    }
  }

  determineApplyButtonDisableState() {
    this.disableApply$.next(!this.appliedFilters?.length && this.nullFilter === 'NOT_APPLIED');
  }

  handleAddCodeFilter(code: Code) {
    this.selectedCodes$.next([...this.selectedCodes$.value, code]);
    this.determineApplyButtonDisableState();
  }

  handleAddDateFilter(dateFilter: RdsDateFilter) {
    this.dateFilters$.next([...this.dateFilters$.value, dateFilter]);
    this.determineApplyButtonDisableState();
  }

  handleAddNumericFilter(numericFilter: RdsNumericFilter) {
    this.numericFilters$.next([...this.numericFilters$.value, numericFilter]);
    this.determineApplyButtonDisableState();
  }

  handleAddTextFilter(textFilter: RdsTextFilter) {
    this.textFilters$.next([...this.textFilters$.value, textFilter]);
    this.determineApplyButtonDisableState();
  }

  /** Close the dialog with the selected filters */
  handleApplyFilter() {
    this.dialogRef.close({ filters: this.appliedFilters, nullFilter: this.nullFilter, excludeSelectedCodes: this.excludeSelected$.value });
  }

  handleClearCodeFilters() {
    this.selectedCodes$.next([]);
    this.determineApplyButtonDisableState();
    this.cdr.markForCheck();
  }

  /**
   * Display the next/previous codes when user paginates
   * @param page PageEvent
   */
  handleCodePageChange(page: PageEvent) {
    this.loadingCodes = true;
    this.codeOffset = page.pageIndex * page.pageSize;
    this.cdr.markForCheck();

    this._searchCodes();
  }

  handleCodeSearch(searchText: string) {
    this.loadingCodes = true;
    // reset the offset when new text is being searched
    this.codeOffset = 0;
    this.codeSearchText = searchText;
    this.cdr.markForCheck();

    this._searchCodes();
  }

  handleCodeSort(direction: SortDirection) {
    this.loadingCodes = true;
    this.sortDirection$.next(direction);
    this._searchCodes();
  }

  handleExcludeSelected(change: MatSlideToggleChange) {
    this.excludeSelected$.next(change.checked);
    this.determineApplyButtonDisableState();
  }

  handleRemoveCodeFilter(code: Code) {
    this.selectedCodes$.next(this.selectedCodes$.value.filter((c) => c.uri !== code.uri));
    this.determineApplyButtonDisableState();
  }

  handleRemoveDateFilter(dateFilter: RdsDateFilter) {
    this.dateFilters$.next(this.dateFilters$.value.filter((n) => n.value !== dateFilter.value || n.operation !== dateFilter.operation));
    this.determineApplyButtonDisableState();
  }

  handleRemoveNumericFilter(numericFilter: RdsNumericFilter) {
    this.numericFilters$.next(
      this.numericFilters$.value.filter((n) => n.value !== numericFilter.value || n.operation !== numericFilter.operation)
    );
    this.determineApplyButtonDisableState();
  }

  handleRemoveTextFilter(textFilter: RdsTextFilter) {
    this.textFilters$.next(this.textFilters$.value.filter((t) => t.text !== textFilter.text && t.parameter !== textFilter.parameter));
    this.determineApplyButtonDisableState();
  }

  ngOnInit() {
    if (this.filterType === 'CODE' && this.data.variable.classificationUri) {
      this.loadingCodes = true;
      this.cdr.markForCheck();

      // Retrieve the Classification & rootCodeCount to determine the display component to use
      this.classificationService
        .getClassification(this.data.variable.classificationUri)
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((classification: Classification) => {
          if (classification?.rootCodeCount) {
            this.hasFewCodes = classification.rootCodeCount <= this.codePageSize;
            this.cdr.markForCheck();
            this.handleCodeSearch('');
          } else {
            this.loadingCodes = false;
            this.cdr.markForCheck();
          }
        });
    }
  }

  /** Call the code search api */
  private _searchCodes() {
    if (!this.data.variable.classificationUri) {
      return;
    }
    // If there is text, wrap with wildcards
    const searchText = this.codeSearchText ? `*${this.codeSearchText}*` : undefined;
    let params: CodeSearchParameters = { searchText, offset: this.codeOffset, limit: this.codePageSize };
    if (this.sortDirection$.valueMTNA) {
      params = { ...params, sortField: 'CODE', sortOrder: this.sortDirection$.valueMTNA };
    }
    this.classificationService
      .searchCodes(this.data.variable.classificationUri, params)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe(this._handleCodeSearchResponse(), this._handleCodeSearchError());
  }

  private _handleCodeSearchResponse() {
    return (response: CodeSearchResponse) => {
      this.foundCodes = response.found;
      this.codes = response.results;
      this.loadingCodes = false;
      this.cdr.markForCheck();
    };
  }

  private _handleCodeSearchError(): (error: unknown) => void {
    return (err) => {
      this.foundCodes = 0;
      this.codes = [];
      this.loadingCodes = false;
      this.errorService.handleError(err);
      this.cdr.markForCheck();
    };
  }
}
